import { css, cx } from "styled-system/css";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { cf } from "@/components/brand/sfturnos-styles";
import { DashboardPageShell } from "@/components/dashboard/dashboard-page-shell";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { BillingCycleToggle, type BillingCycle } from "@/components/ui/billing-cycle-toggle";
import { useToast } from "@/hooks/useToast";

const ENTERPRISE_INCLUDED_AGENDAS = 3;
const ENTERPRISE_MIN_AGENDAS = 4;

type CoreBillingStatus = {
  active: boolean;
  status: "free" | "pending" | "authorized" | "active" | "paused" | "cancelled" | "past_due";
  planSlug: string | null;
  planName: string | null;
  frequency: string | null;
  subscriptionId: string | null;
  extraUnits: number;
  daysRemaining: number | null;
  lastPaymentAt: string | null;
  lastPaymentAmount: number | null;
  amount: number | null;
  /** Checkout Pro: hasta cuándo está pago el período actual. Null en suscripciones legacy. */
  currentPeriodEnd: string | null;
} | null;

type AddonBillingStatus = {
  planSlug: string;
  planName: string;
  active: boolean;
  status: "pending" | "authorized" | "active" | "paused" | "cancelled" | "past_due";
  subscriptionId: string;
  lastPaymentAt: string | null;
  lastPaymentAmount: number | null;
  amount: number;
  currentPeriodEnd: string | null;
  featureSlug: string | null;
};

type SubscriptionData = {
  plan: "FREE" | "PRO" | "ENTERPRISE";
  planExpiresAt: string | null;
  agendasAllowed: number | null;
  pendingPlanPayment: boolean;
  trialDaysLeft: number | null;
  billing: { core: CoreBillingStatus; addons: AddonBillingStatus[] };
  billingPlanIds: Record<string, string>;
  marketTrials: { featureName: string; monthlyPrice: number; daysLeft: number | null }[];
  marketExpired: { featureSlug: string; featureName: string; monthlyPrice: number }[];
  /** Tarjeta guardada (card on file) del dueño de la cuenta, si ya pagó algo antes. */
  mpSavedCard: { id: string; last4: string | null; brand: string | null } | null;
};

type PlanPricingItem = {
  plan: "FREE" | "PRO" | "ENTERPRISE";
  monthlyPrice: number;
  annualPrice: number;
  extraAgendaMonthly: number;
  extraAgendaAnnual: number;
};

type PublicPricingData = {
  pricing: PlanPricingItem[];
  annualBillingEnabled: boolean;
};

type DiscountResult = {
  code: string;
  percentOff: number;
  durationDays: number | null;
  applicablePlanSlugs: string[];
};

const planData: Record<string, { label: string; description: string; agendas: string; bookings: string; features: string[]; color: string }> = {
  FREE: {
    label: "Free",
    description: "Para empezar sin compromiso. Ideal para probar la plataforma.",
    agendas: "1 agenda",
    bookings: "100 turnos / mes",
    features: ["Página de reservas pública", "Notificaciones por email", "Sin integraciones de pago"],
    color: "#64748b",
  },
  PRO: {
    label: "Pro",
    description: "Turnos ilimitados, hasta 3 agendas y todas las integraciones.",
    agendas: "3 agendas",
    bookings: "Turnos ilimitados",
    features: ["Todo lo del plan Free", "Notificaciones por WhatsApp", "Pagos con Mercado Pago", "Recordatorios automáticos"],
    color: "token(colors.brand.600)",
  },
  ENTERPRISE: {
    label: "Enterprise",
    description: "Para clínicas y estudios grandes con múltiples agendas y profesionales.",
    agendas: "4+ agendas",
    bookings: "Turnos ilimitados",
    features: ["Todo lo del plan Pro", "Agendas ilimitadas", "Acompañamiento dedicado"],
    color: "#7c3aed",
  },
};

function formatPrice(n: number) {
  return new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }).format(n);
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("es-AR", { day: "numeric", month: "long", year: "numeric" });
}

async function parseErrorMessage(r: Response, fallback: string): Promise<string> {
  const j = (await r.json().catch(() => ({}))) as { error?: string };
  // El backend a veces devuelve detalle técnico (ids, mensajes de MP); no se lo mostramos al usuario.
  if (j.error && j.error.length < 140 && !/[{}[\]]/.test(j.error)) return j.error;
  return fallback;
}

/** Una fila del "Resumen de tu suscripción": plan o módulo de Market, con su precio y — si hace
 *  falta pagarlo (nunca pagado, trial vencido, checkout pending) — un botón "Activar" propio. */
type BillingSummaryLine = {
  key: string;
  label: string;
  amount: number;
  note: "prueba" | "vencido" | null;
  action?: { busy: boolean; onClick: () => void };
};

function BillingLineRow({ line, anyActionBusy }: { line: BillingSummaryLine; anyActionBusy: boolean }) {
  return (
    <div className={css({ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "sm", gap: "3" })}>
      <span className={css({ color: "#334155" })}>
        {line.label}
        {line.note ? (
          <span className={css({ color: line.note === "vencido" ? "#92400e" : "#94a3b8" })}> ({line.note})</span>
        ) : null}
      </span>
      <span className={css({ display: "flex", alignItems: "center", gap: "2", flexShrink: 0 })}>
        <span className={css({ color: "#0f172a", fontWeight: "medium" })}>{formatPrice(line.amount)}/mes</span>
        {line.action ? (
          <button
            type="button"
            disabled={anyActionBusy}
            onClick={line.action.onClick}
            className={css({
              fontSize: "xs",
              fontWeight: "semibold",
              color: "white",
              bg: "token(colors.brand.600)",
              px: "2.5",
              py: "1",
              rounded: "md",
              border: "none",
              cursor: anyActionBusy ? "not-allowed" : "pointer",
              opacity: anyActionBusy && !line.action.busy ? 0.5 : 1,
              transition: "opacity 120ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
              _active: { transform: anyActionBusy ? "none" : "scale(0.97)" },
            })}
          >
            {line.action.busy ? "Procesando…" : "Activar"}
          </button>
        ) : null}
      </span>
    </div>
  );
}

export function DashboardSubscription() {
  const { refresh } = useAuth();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  // Viene de "Activar suscripción" en el modal de trial vencido, con el plan que el usuario ya
  // tenía en su trial — así esta página resalta ese plan en la comparativa en vez de dejar que la
  // calculadora de Enterprise (visualmente más grande) sea lo primero que el usuario nota y termine
  // pagando un plan distinto al que buscaba.
  const highlightPlan = searchParams.get("highlight") === "enterprise" ? "enterprise" : "pro";
  const proRowRef = useRef<HTMLDivElement>(null);
  const [enterpriseOpen, setEnterpriseOpen] = useState(false);
  const [sub, setSub] = useState<SubscriptionData | null>(null);
  const [pricing, setPricing] = useState<PublicPricingData | null>(null);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>("monthly");
  const [enterpriseAgendas, setEnterpriseAgendas] = useState(ENTERPRISE_MIN_AGENDAS);
  const [busy, setBusy] = useState<"pro" | "enterprise" | "enterprise-update" | "cancel-pending" | null>(null);
  // featureSlug del módulo de Market cuyo checkout se está iniciando desde el resumen de
  // facturación (botón "Activar módulo" en una línea vencida) — null cuando no hay ninguno en curso.
  const [moduleCheckoutBusy, setModuleCheckoutBusy] = useState<string | null>(null);
  const subscribingRef = useRef(false);
  const [discountInput, setDiscountInput] = useState("");
  const [discountResult, setDiscountResult] = useState<DiscountResult | null>(null);
  const [discountError, setDiscountError] = useState<string | null>(null);
  const [validatingDiscount, setValidatingDiscount] = useState(false);

  // Guard contra respuestas fuera de orden: si dos loadSubscription() se solapan (poll en curso +
  // una revalidación disparada por pageshow/foco), solo la respuesta de la request más reciente
  // puede actualizar el estado. Sin esto, una response vieja que llega tarde podría pisar datos
  // frescos con un estado "pending" desactualizado.
  const requestSeq = useRef(0);

  const loadSubscription = useCallback(async (): Promise<CoreBillingStatus | undefined> => {
    const seq = ++requestSeq.current;
    const r = await fetchWithSessionSync("/api/dashboard/subscription", { credentials: "include" }, refresh);
    if (!r.ok) return undefined;
    const data = (await r.json()) as SubscriptionData;
    if (seq !== requestSeq.current) return data.billing.core;
    setSub(data);
    if (data.billing.core?.active && data.billing.core.extraUnits) {
      setEnterpriseAgendas(ENTERPRISE_INCLUDED_AGENDAS + data.billing.core.extraUnits);
    } else if (data.agendasAllowed != null && data.agendasAllowed > ENTERPRISE_INCLUDED_AGENDAS) {
      // Todavía no hay suscripción de MP autorizada (ej: cuenta en trial que eligió agendas
      // extra al registrarse) — precargar desde agendasAllowed para que el monto a cobrar cuando
      // se suscriba de verdad coincida con lo que ya eligió, sin que nadie tenga que reconfigurarlo.
      setEnterpriseAgendas(data.agendasAllowed);
    }
    return data.billing.core;
  }, [refresh]);

  useEffect(() => {
    void loadSubscription();
  }, [loadSubscription]);

  // Si el usuario vuelve del checkout de Mercado Pago con la flecha "atrás" del navegador (en vez
  // del botón de vuelta propio del sitio), el navegador puede restaurar esta página desde el
  // bfcache: la página queda congelada tal cual estaba antes de navegar a MP (incluyendo el estado
  // "Procesando el pago..."), sin volver a montar el componente ni disparar sus efectos. El evento
  // `pageshow` con `event.persisted === true` es la forma estándar de detectar esa restauración y
  // forzar una revalidación real contra el backend.
  useEffect(() => {
    function onPageShow(event: PageTransitionEvent) {
      if (event.persisted) void loadSubscription();
    }
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [loadSubscription]);

  // Fuerza una resincronización real contra MP (no solo releer nuestra DB) para una suscripción
  // puntual. Se usa cuando queda "pending" demasiado tiempo, por si el webhook nunca llegó o
  // falló silenciosamente — así ninguna cuenta queda colgada esperando un webhook que no llega,
  // sin que nadie tenga que notarlo y avisar a mano.
  const resyncSubscription = useCallback(
    async (subscriptionId: string): Promise<CoreBillingStatus | undefined> => {
      await fetchWithSessionSync(
        `/api/billing/subscriptions/${subscriptionId}/resync`,
        { method: "POST", credentials: "include" },
        refresh,
      ).catch(() => undefined);
      return loadSubscription();
    },
    [refresh, loadSubscription],
  );

  // Mientras el pago está "procesando" (pago hecho en MP, webhook todavía no llegó), reconsultamos
  // cada pocos segundos para reflejar la confirmación automáticamente, sin que el cliente tenga que
  // refrescar la página a mano. Se corta sola cuando deja de estar pending o tras un tiempo máximo.
  // Cada RESYNC_EVERY intentos (~32s), en vez de releer nuestra DB (que seguiría "pending" si el
  // webhook nunca llegó), se fuerza una resincronización real contra MP.
  useEffect(() => {
    if (sub?.billing.core?.status !== "pending") return;
    const subscriptionId = sub.billing.core.subscriptionId;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 20;
    const RESYNC_EVERY = 8;
    const interval = setInterval(() => {
      attempts += 1;
      if (attempts > maxAttempts) {
        clearInterval(interval);
        return;
      }
      const action =
        attempts % RESYNC_EVERY === 0 && subscriptionId
          ? resyncSubscription(subscriptionId)
          : loadSubscription();
      void action.then((core) => {
        if (!cancelled && core && core.status !== "pending") clearInterval(interval);
      });
    }, 4000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [sub?.billing.core?.status, sub?.billing.core?.subscriptionId, loadSubscription, resyncSubscription]);

  useEffect(() => {
    let cancelled = false;
    void fetch("/api/public/plan-pricing", { cache: "no-store" })
      .then(async (r) => {
        if (!r.ok) throw new Error("pricing");
        return (await r.json()) as PublicPricingData;
      })
      .then((d) => {
        if (!cancelled) setPricing(d);
      })
      .catch(() => {
        if (!cancelled) setPricing({ pricing: [], annualBillingEnabled: false });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const proPricing = pricing?.pricing.find((p) => p.plan === "PRO") ?? null;
  const entPricing = pricing?.pricing.find((p) => p.plan === "ENTERPRISE") ?? null;

  const enterpriseExtraAgendas = Math.max(0, enterpriseAgendas - ENTERPRISE_INCLUDED_AGENDAS);
  const enterpriseBasePrice = entPricing ? (billingCycle === "annual" ? entPricing.annualPrice : entPricing.monthlyPrice) : 25_000;
  const enterpriseExtraPrice = entPricing ? (billingCycle === "annual" ? entPricing.extraAgendaAnnual : entPricing.extraAgendaMonthly) : 30_000;
  const enterpriseMonthlyPrice = enterpriseBasePrice + enterpriseExtraAgendas * enterpriseExtraPrice;
  const proPrice = proPricing ? (billingCycle === "annual" ? proPricing.annualPrice : proPricing.monthlyPrice) : null;

  const proSlug = `pro-${billingCycle}`;
  const enterpriseSlug = `enterprise-${billingCycle}`;
  const discountAppliesToPro = discountResult?.applicablePlanSlugs.includes(proSlug) ?? false;
  const discountAppliesToEnterprise = discountResult?.applicablePlanSlugs.includes(enterpriseSlug) ?? false;
  const proPriceWithDiscount =
    discountAppliesToPro && proPrice != null && discountResult
      ? Math.round(proPrice * (1 - discountResult.percentOff / 100))
      : null;
  const enterpriseMonthlyPriceWithDiscount =
    discountAppliesToEnterprise && discountResult
      ? Math.round(enterpriseMonthlyPrice * (1 - discountResult.percentOff / 100))
      : null;
  const discountDurationLabel = discountResult?.durationDays
    ? `por ${discountResult.durationDays} día${discountResult.durationDays === 1 ? "" : "s"}`
    : "mientras dure la suscripción";

  const core = sub?.billing.core ?? null;
  const addons = sub?.billing.addons ?? [];
  // Módulos que efectivamente están generando un cobro (activo o en gracia) — se suman al total.
  // "active" es Checkout Pro (flujo actual); "authorized" es Preapproval (legacy).
  const billedAddons = addons.filter((a) => a.status === "authorized" || a.status === "active" || a.status === "past_due");
  const pendingAddons = addons.filter((a) => a.status === "pending");
  const corePrefix = core?.planSlug?.split("-")[0] ?? null;
  const hasCoreInFlight = core != null && ["authorized", "active", "pending", "past_due"].includes(core.status);
  const isProActive = hasCoreInFlight && corePrefix === "pro";
  const isEnterpriseActive = hasCoreInFlight && corePrefix === "enterprise";

  // La calculadora de Enterprise arranca colapsada salvo que el usuario ya esté en ese plan o haya
  // llegado explícitamente buscando Enterprise (?highlight=enterprise) — evita que compita
  // visualmente con "Elegir Pro" cuando lo que busca es activar Pro.
  useEffect(() => {
    if (isEnterpriseActive || highlightPlan === "enterprise") setEnterpriseOpen(true);
  }, [isEnterpriseActive, highlightPlan]);

  // Si llega buscando un plan puntual (desde el modal de trial vencido), lleva la vista directo a
  // esa fila de la comparativa en vez de dejar que el usuario tenga que scrollear a encontrarla.
  useEffect(() => {
    if (!sub || hasCoreInFlight) return;
    if (highlightPlan === "pro") {
      proRowRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, [sub, hasCoreInFlight, highlightPlan]);

  async function applyDiscountCode() {
    const code = discountInput.trim();
    if (!code) return;
    setValidatingDiscount(true);
    setDiscountError(null);
    const r = await fetchWithSessionSync(
      "/api/billing/discount-codes/validate",
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      },
      refresh,
    );
    setValidatingDiscount(false);
    if (!r.ok) {
      setDiscountResult(null);
      setDiscountError("No pudimos validar el código. Podés seguir pagando a precio normal.");
      return;
    }
    const j = (await r.json()) as
      | { valid: true; percentOff: number; durationDays: number | null; applicablePlanSlugs: string[] }
      | { valid: false; error: string };
    if (!j.valid) {
      setDiscountResult(null);
      setDiscountError(j.error);
      return;
    }
    setDiscountResult({
      code,
      percentOff: j.percentOff,
      durationDays: j.durationDays,
      applicablePlanSlugs: j.applicablePlanSlugs,
    });
  }

  // Inicia el checkout del plan elegido: crea el Preapproval "pending" en el backend y redirige al
  // checkout hospedado de Mercado Pago (init_point). La suscripción queda "pending" hasta que el
  // usuario complete el alta ahí — el webhook (o el polling de loadSubscription) la pasa a
  // "authorized" cuando MP confirma.
  async function subscribeTo(kind: "pro" | "enterprise") {
    if (!sub) return;
    const slug = `${kind}-${billingCycle}`;
    const planId = sub.billingPlanIds[slug];
    if (!planId) {
      toast.error("Este plan no está disponible todavía. Contactanos para activarlo.");
      return;
    }
    // Evita dos POSTs por un doble click mientras se genera el init_point.
    if (subscribingRef.current) return;
    subscribingRef.current = true;
    setBusy(kind);
    try {
      const discountAppliesHere = discountResult?.applicablePlanSlugs.includes(slug) ?? false;
      const r = await fetchWithSessionSync(
        "/api/billing/subscribe",
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            planId,
            ...(kind === "enterprise" ? { extraUnits: enterpriseExtraAgendas } : {}),
            ...(discountAppliesHere ? { discountCode: discountResult!.code } : {}),
          }),
        },
        refresh,
      );
      if (!r.ok) {
        toast.error(await parseErrorMessage(r, "No pudimos iniciar el pago. Probá de nuevo en un momento."));
        subscribingRef.current = false;
        setBusy(null);
        return;
      }
      const { initPoint } = (await r.json()) as { initPoint: string; subscriptionId: string };
      window.location.href = initPoint;
    } catch {
      toast.error("No pudimos iniciar el pago. Probá de nuevo en un momento.");
      subscribingRef.current = false;
      setBusy(null);
    }
  }

  async function updateEnterpriseAgendas() {
    if (!core?.subscriptionId) return;
    setBusy("enterprise-update");
    const r = await fetchWithSessionSync(
      `/api/billing/subscriptions/${core.subscriptionId}/agendas`,
      {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ extraUnits: enterpriseExtraAgendas }),
      },
      refresh,
    );
    setBusy(null);
    if (!r.ok) {
      toast.error(await parseErrorMessage(r, "No pudimos actualizar la cantidad de agendas."));
      return;
    }
    toast.success("Cantidad de agendas actualizada.");
  }

  // Cancela un intento de pago sin resolver (cancela el preapproval en MP también, no solo acá),
  // para que el cliente pueda arrancar de cero con un click en vez de quedar pegado a "pending".
  async function cancelPendingAttempt() {
    if (!core?.subscriptionId) return;
    setBusy("cancel-pending");
    const r = await fetchWithSessionSync(
      `/api/billing/subscriptions/${core.subscriptionId}`,
      {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "cancel" }),
      },
      refresh,
    );
    setBusy(null);
    if (!r.ok) {
      toast.error(await parseErrorMessage(r, "No pudimos cancelar el intento. Probá de nuevo en un momento."));
      return;
    }
    toast.success("Intento cancelado. Ya podés arrancar de nuevo cuando quieras.");
    void loadSubscription();
  }

  // Inicia el checkout de un módulo de Market vencido desde el resumen de facturación (botón
  // "Activar módulo") — mismo endpoint que usa la página de Market, cobro recurrente independiente
  // del plan. Igual que subscribeTo: crea el Preapproval "pending" y redirige al init_point de MP.
  async function startModuleCheckout(featureSlug: string) {
    setModuleCheckoutBusy(featureSlug);
    try {
      const r = await fetchWithSessionSync(
        "/api/market/checkout",
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ featureSlug }),
        },
        refresh,
      );
      if (!r.ok) {
        toast.error(await parseErrorMessage(r, "No pudimos iniciar el pago del módulo. Probá de nuevo en un momento."));
        setModuleCheckoutBusy(null);
        return;
      }
      const { initPoint } = (await r.json()) as { initPoint: string; subscriptionId: string };
      window.location.href = initPoint;
    } catch {
      toast.error("No pudimos iniciar el pago del módulo. Probá de nuevo en un momento.");
      setModuleCheckoutBusy(null);
    }
  }

  if (!sub) {
    return (
      <DashboardPageShell eyebrow="Plan" title="Suscripción">
        <LoadingFrame variant="light" minHeight={240} />
      </DashboardPageShell>
    );
  }

  const plan = sub.plan as keyof typeof planData;
  const planInfo = planData[plan] ?? planData.FREE;

  // Resumen de lo que se factura (o facturará) por esta cuenta: plan + módulos de Market — cada uno
  // es un cobro recurrente independiente en Mercado Pago, acá se muestran juntos para que el cliente
  // sepa el total antes de ir a pagar. Cada ítem lleva su propio botón "Activar" cuando hace falta
  // pagarlo (nunca pagado, trial vencido, o un intento de checkout que quedó pending); los ítems que
  // ya están activos y pagándose (o en trial vigente) no llevan botón. Usa el monto real de la
  // BillingSubscription cuando ya existe y, si todavía no hay ninguna creada, cae al precio de
  // catálogo (PlanPricing / MarketFeature) como estimado, para que el desglose aparezca en cualquier
  // estado con plan Pro.

  // Deshabilita todos los botones "Activar" del resumen mientras cualquiera de ellos está en curso
  // (evita doble click disparando dos checkouts distintos a la vez).
  const anyActionBusy = busy !== null || moduleCheckoutBusy !== null;

  const corePlanKind: "pro" | "enterprise" | null =
    corePrefix === "pro" || corePrefix === "enterprise"
      ? corePrefix
      : sub.plan === "PRO"
        ? "pro"
        : sub.plan === "ENTERPRISE"
          ? "enterprise"
          : null;

  const coreLine: BillingSummaryLine | null = (() => {
    const label = sub.plan === "ENTERPRISE" ? "Plan Enterprise" : "Plan Pro";

    if (core?.amount != null) {
      // Al día (core.active ya contempla currentPeriodEnd para Checkout Pro, no solo el status) o
      // en gracia (past_due, legacy): se sigue cobrando, no hace falta ningún botón acá — el estado
      // de pago en problemas ya se explica en la tarjeta "Estado del pago".
      if (core.active || core.status === "past_due") {
        return { key: "core", label, amount: core.amount, note: null };
      }
      // pending (nunca pagó) o active-pero-vencido (Checkout Pro, currentPeriodEnd pasado): en
      // ambos casos se retoma/renueva con el mismo botón "Activar".
      return {
        key: "core",
        label,
        amount: core.amount,
        note: "vencido",
        action: corePlanKind
          ? {
              busy: busy === corePlanKind,
              onClick: () => void subscribeTo(corePlanKind),
            }
          : undefined,
      };
    }

    // No hay ninguna BillingSubscription todavía.
    if (sub.plan !== "PRO" && sub.plan !== "ENTERPRISE") return null;
    const fallbackAmount = sub.plan === "ENTERPRISE" ? enterpriseMonthlyPrice : proPrice;
    if (fallbackAmount == null) return null;

    const trialActive = sub.trialDaysLeft != null && sub.trialDaysLeft > 0;
    if (trialActive) {
      return { key: "core", label, amount: fallbackAmount, note: "prueba" };
    }
    return {
      key: "core",
      label,
      amount: fallbackAmount,
      note: "vencido",
      action: corePlanKind
        ? { busy: busy === corePlanKind, onClick: () => void subscribeTo(corePlanKind) }
        : undefined,
    };
  })();

  const billedAddonNames = new Set(billedAddons.map((a) => a.planName));
  const pendingAddonNames = new Set(pendingAddons.map((a) => a.planName));
  const addonLines: BillingSummaryLine[] = [
    // Autorizado o en gracia: se sigue cobrando, sin botón.
    ...billedAddons.map((a): BillingSummaryLine => ({ key: a.subscriptionId, label: a.planName, amount: a.amount, note: null })),
    // pending: checkout de este módulo iniciado y nunca completado — se retoma con "Activar".
    ...pendingAddons.map((a): BillingSummaryLine => ({
      key: a.subscriptionId,
      label: a.planName,
      amount: a.amount,
      note: "vencido",
      action: a.featureSlug
        ? {
            busy: moduleCheckoutBusy === a.featureSlug,
            onClick: () => void startModuleCheckout(a.featureSlug as string),
          }
        : undefined,
    })),
    // Módulos con acceso otorgado a mano (trial vigente) que todavía no generaron una
    // BillingSubscription — se muestran como estimado, sin botón (todavía no hay nada que pagar).
    ...sub.marketTrials
      .filter((t) => !billedAddonNames.has(t.featureName) && !pendingAddonNames.has(t.featureName))
      .map((t): BillingSummaryLine => ({
        key: `trial-${t.featureName}`,
        label: t.featureName,
        amount: t.monthlyPrice,
        note: "prueba",
      })),
    // Módulos otorgados a mano (trial o activación manual) cuyo período de acceso ya venció —
    // siguen sumando al total y llevan un botón "Activar" directo para pagarlos ya mismo.
    ...sub.marketExpired.map((m): BillingSummaryLine => ({
      key: `expired-${m.featureSlug}`,
      label: m.featureName,
      amount: m.monthlyPrice,
      note: "vencido",
      action: {
        busy: moduleCheckoutBusy === m.featureSlug,
        onClick: () => void startModuleCheckout(m.featureSlug),
      },
    })),
  ];

  const billingSummaryTotal = (coreLine?.amount ?? 0) + addonLines.reduce((sum, l) => sum + l.amount, 0);

  const statusBadge = (() => {
    if (!core || core.status === "free") return null;
    if (core.status === "authorized") {
      return { tone: "success" as const, label: "Al día" };
    }
    if (core.status === "active") {
      // Checkout Pro: sin cobro automático — currentPeriodEnd es la fecha real hasta la que hay
      // acceso (core.active ya la contempla, ver isSubscriptionCurrentlyActive en billingService.ts).
      // El status en sí se queda en "active" para siempre hasta que el usuario vuelva a pagar (no
      // hay downgrade automático todavía), así que acá es donde se nota si ya venció o no.
      if (core.active) {
        return {
          tone: "success" as const,
          label: "Al día",
          message: core.currentPeriodEnd ? `Vence el ${formatDate(core.currentPeriodEnd)}.` : undefined,
        };
      }
      return {
        tone: "warning" as const,
        label: "Pago vencido",
        message: core.currentPeriodEnd
          ? `Tu período pago venció el ${formatDate(core.currentPeriodEnd)}. Volvé a pagar para seguir usando tu plan.`
          : "Tu período pago venció. Volvé a pagar para seguir usando tu plan.",
      };
    }
    if (core.status === "past_due") {
      const days = core.daysRemaining ?? 0;
      return {
        tone: "warning" as const,
        label: "Pago pendiente",
        message: `Tu pago falló. Tenés ${days === 1 ? "1 día" : `${days} días`} para regularizar antes de volver al plan Free.`,
      };
    }
    if (core.status === "cancelled") {
      return {
        tone: "danger" as const,
        label: "Cancelado por falta de pago",
        message: "Tu plan fue cancelado por falta de pago. Podés volver a suscribirte cuando quieras.",
      };
    }
    if (core.status === "pending") {
      return {
        tone: "neutral" as const,
        label: "Esperando que completes el pago",
        message:
          "Todavía no se registró ningún cobro — no se te cobra nada hasta que termines de cargar tu tarjeta en Mercado Pago. Si querés, podés retomar ese pago o cancelar el intento y empezar de nuevo.",
      };
    }
    if (core.status === "paused") {
      return { tone: "neutral" as const, label: "Pausado" };
    }
    return null;
  })();

  const toneColors: Record<string, { bg: string; fg: string; border: string }> = {
    success: { bg: "#dcfce7", fg: "#14532d", border: "#bbf7d0" },
    warning: { bg: "#fef3c7", fg: "#92400e", border: "#fcd34d" },
    danger: { bg: "#fee2e2", fg: "#991b1b", border: "#fecaca" },
    neutral: { bg: "#f1f5f9", fg: "#475569", border: "#e2e8f0" },
  };

  return (
    <DashboardPageShell
      eyebrow="Plan"
      title="Suscripción"
      description="Resumen de tu plan actual con SFTURNOS."
    >
      <div className={css({ display: "flex", flexDir: "column", gap: "6", maxW: "36rem" })}>
        {sub.pendingPlanPayment && (sub.plan === "PRO" || sub.plan === "ENTERPRISE") ? (
          <div
            className={css({
              p: "4",
              rounded: "lg",
              borderWidth: "1px",
              borderColor: "#fcd34d",
              bg: "#fffbeb",
            })}
          >
            <p className={css({ fontSize: "sm", color: "#92400e", lineHeight: "1.6", m: 0 })}>
              <strong>Pago de plan:</strong> tu cuenta está en {sub.plan === "PRO" ? "Pro" : "Enterprise"} y figura como{" "}
              <strong>pendiente de pago</strong> hasta que coordines la mensualidad con SFTURNOS. Escribinos o acordá el medio de pago; cuando
              esté regularizado, el equipo puede quitar este aviso del sistema.
            </p>
            <a
              href="mailto:turnos@sftdevelopment.com?subject=Pago%20plan%20SFTURNOS"
              className={css({ display: "inline-block", mt: "3", fontSize: "sm", fontWeight: "semibold", color: "token(colors.brand.600)" })}
            >
              Escribir a turnos@sftdevelopment.com
            </a>
          </div>
        ) : null}

        {/* Card plan actual */}
        <div
          className={cx(
            cf.dashSurface,
            css({
              p: "6",
              borderLeftWidth: "4px",
              borderLeftColor: planInfo.color,
            }),
          )}
        >
          <p className={css({ fontSize: "xs", color: "#64748b", mb: "1" })}>Plan actual</p>
          <p className={css({ fontSize: "2xl", fontWeight: "bold", color: "#0f172a", letterSpacing: "tight" })}>
            {planInfo.label}
          </p>
          <p className={css({ fontSize: "sm", color: "#64748b", mt: "2", lineHeight: "1.6" })}>{planInfo.description}</p>

          <div className={css({ mt: "4", display: "flex", flexWrap: "wrap", gap: "3" })}>
            <span className={css({ fontSize: "xs", bg: "#f1f5f9", color: "#475569", px: "3", py: "1.5", rounded: "full", fontWeight: "semibold" })}>
              📅 {plan === "ENTERPRISE" && sub.agendasAllowed != null ? `${sub.agendasAllowed} agendas` : planInfo.agendas}
            </span>
            <span className={css({ fontSize: "xs", bg: "#f1f5f9", color: "#475569", px: "3", py: "1.5", rounded: "full", fontWeight: "semibold" })}>
              🗓 {planInfo.bookings}
            </span>
          </div>
        </div>

        {/* Resumen de facturación: plan + módulos de Market, cada uno con su propio botón "Activar"
            cuando hace falta pagarlo. Siempre visible mientras haya un plan Pro/Enterprise elegido,
            sin importar el estado de pago (trial activo, trial vencido, pago pendiente o suscripción
            activa) — va antes de la comparativa de planes para que el cliente sepa qué va a pagar,
            y por cuál ítem, antes de ir a Mercado Pago. */}
        {coreLine || addonLines.length > 0 ? (
          <div className={cx(cf.dashSurface, css({ p: "6" }))}>
            <p className={css({ fontSize: "sm", fontWeight: "semibold", color: "#334155", mb: "3" })}>
              Resumen de tu suscripción
            </p>
            <div className={css({ display: "flex", flexDir: "column", gap: "2" })}>
              {coreLine ? <BillingLineRow line={coreLine} anyActionBusy={anyActionBusy} /> : null}
              {addonLines.length > 0 ? (
                <>
                  <p
                    className={css({
                      fontSize: "10px",
                      fontWeight: "bold",
                      letterSpacing: "wide",
                      color: "#94a3b8",
                      textTransform: "uppercase",
                      mt: "2",
                    })}
                  >
                    Market
                  </p>
                  {addonLines.map((l) => (
                    <BillingLineRow key={l.key} line={l} anyActionBusy={anyActionBusy} />
                  ))}
                </>
              ) : null}
            </div>
            <div
              className={css({
                display: "flex",
                justifyContent: "space-between",
                fontSize: "sm",
                fontWeight: "bold",
                color: "#0f172a",
                mt: "3",
                pt: "3",
                borderTopWidth: "1px",
                borderColor: "#f1f5f9",
              })}
            >
              <span>Total</span>
              <span>{formatPrice(billingSummaryTotal)}/mes</span>
            </div>
            <p className={css({ fontSize: "xs", color: "#94a3b8", mt: "3" })}>
              El plan y cada módulo de Market son cobros recurrentes independientes en Mercado Pago.
            </p>
          </div>
        ) : null}

        {/* Estado de pago + último pago */}
        {statusBadge ? (
          <div className={cx(cf.dashSurface, css({ p: "6" }))}>
            <p className={css({ fontSize: "sm", fontWeight: "semibold", color: "#334155", mb: "3" })}>Estado del pago</p>
            <span
              className={css({
                display: "inline-flex",
                fontSize: "xs",
                fontWeight: "bold",
                px: "3",
                py: "1",
                rounded: "full",
                bg: toneColors[statusBadge.tone].bg,
                color: toneColors[statusBadge.tone].fg,
              })}
            >
              {statusBadge.label}
            </span>
            {statusBadge.message ? (
              <p className={css({ fontSize: "sm", color: toneColors[statusBadge.tone].fg, mt: "3", lineHeight: "1.6" })}>
                {statusBadge.message}
              </p>
            ) : null}

            {core?.status === "pending" && corePrefix ? (
              <div className={css({ display: "flex", gap: "3", mt: "4" })}>
                <button
                  type="button"
                  disabled={busy !== null}
                  onClick={() => void subscribeTo(corePrefix as "pro" | "enterprise")}
                  className={css({
                    px: "4", py: "2", rounded: "lg", fontSize: "sm", fontWeight: "semibold",
                    color: "white", bg: "token(colors.brand.600)", border: "none",
                    cursor: busy !== null ? "not-allowed" : "pointer",
                    opacity: busy !== null ? 0.55 : 1,
                  })}
                >
                  {busy === corePrefix ? "Procesando…" : "Retomar pago"}
                </button>
                <button
                  type="button"
                  disabled={busy !== null}
                  onClick={() => void cancelPendingAttempt()}
                  className={css({
                    px: "4", py: "2", rounded: "lg", fontSize: "sm", fontWeight: "semibold",
                    color: "#991b1b", bg: "transparent", borderWidth: "1px", borderColor: "#fecaca",
                    cursor: busy !== null ? "not-allowed" : "pointer",
                    opacity: busy !== null ? 0.55 : 1,
                  })}
                >
                  {busy === "cancel-pending" ? "Cancelando…" : "Cancelar este intento"}
                </button>
              </div>
            ) : null}

            {core?.lastPaymentAt ? (
              <p className={css({ fontSize: "sm", color: "#64748b", mt: "4", pt: "4", borderTopWidth: "1px", borderColor: "#f1f5f9" })}>
                Último pago: <strong>{formatDate(core.lastPaymentAt)}</strong>
                {core.lastPaymentAmount != null ? <> · {formatPrice(core.lastPaymentAmount)}</> : null}
              </p>
            ) : (
              <p className={css({ fontSize: "xs", color: "#94a3b8", mt: "4" })}>Sin cobros registrados todavía.</p>
            )}
          </div>
        ) : null}

        {/* Período de prueba de cuenta */}
        {sub.trialDaysLeft != null ? (
          <div className={css({ p: "4", rounded: "lg", borderWidth: "1px", borderColor: "#bfdbfe", bg: "#eff6ff" })}>
            <p className={css({ fontSize: "sm", color: "#1e40af", lineHeight: "1.6", m: 0 })}>
              Estás en período de prueba gratuita. Te quedan{" "}
              <strong>{sub.trialDaysLeft === 1 ? "1 día" : `${sub.trialDaysLeft} días`}</strong>.
            </p>
          </div>
        ) : null}

        {/* Módulos de Market en prueba */}
        {sub.marketTrials.length > 0 ? (
          <div className={cx(cf.dashSurface, css({ p: "6" }))}>
            <p className={css({ fontSize: "sm", fontWeight: "semibold", color: "#334155", mb: "3" })}>Módulos en prueba</p>
            <div className={css({ display: "flex", flexDir: "column", gap: "2" })}>
              {sub.marketTrials.map((t) => (
                <div key={t.featureName} className={css({ display: "flex", justifyContent: "space-between", fontSize: "sm" })}>
                  <span className={css({ color: "#334155" })}>{t.featureName}</span>
                  <span className={css({ color: "#92400e", fontWeight: "semibold" })}>
                    {t.daysLeft == null ? "En prueba" : t.daysLeft === 0 ? "Último día" : `${t.daysLeft}d restantes`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* Toggle mensual/anual */}
        {pricing?.annualBillingEnabled ? (
          <div className={css({ display: "flex", justifyContent: "center" })}>
            <BillingCycleToggle value={billingCycle} onChange={setBillingCycle} />
          </div>
        ) : null}

        {/* Código de descuento — visible siempre, incluso con un plan pago activo, por si en el
            futuro hay códigos aplicables a upgrades o renovaciones. */}
        <div className={cx(cf.dashSurface, css({ p: "6" }))}>
          <p className={css({ fontSize: "sm", fontWeight: "semibold", color: "#334155", mb: "3" })}>
            ¿Tenés un código de descuento?
          </p>
          <div className={css({ display: "flex", gap: "2" })}>
            <input
              value={discountInput}
              onChange={(e) => {
                setDiscountInput(e.target.value);
                setDiscountResult(null);
                setDiscountError(null);
              }}
              placeholder="Código"
              className={css({
                flex: "1",
                h: "10",
                px: "3",
                rounded: "lg",
                borderWidth: "1px",
                borderColor: "#e2e8f0",
                fontSize: "sm",
              })}
            />
            <button
              type="button"
              disabled={validatingDiscount || !discountInput.trim()}
              onClick={() => void applyDiscountCode()}
              className={css({
                px: "4",
                h: "10",
                rounded: "lg",
                fontSize: "sm",
                fontWeight: "semibold",
                color: "white",
                bg: "token(colors.brand.600)",
                border: "none",
                cursor: validatingDiscount || !discountInput.trim() ? "not-allowed" : "pointer",
                opacity: validatingDiscount || !discountInput.trim() ? 0.55 : 1,
              })}
            >
              {validatingDiscount ? "Validando…" : "Aplicar"}
            </button>
          </div>
          {discountError ? (
            <p className={css({ fontSize: "sm", color: "#dc2626", mt: "2" })}>{discountError}</p>
          ) : null}
          {discountResult ? (
            <p className={css({ fontSize: "sm", color: "#15803d", mt: "2" })}>
              Código <strong>{discountResult.code}</strong> aplicado: {discountResult.percentOff}% de descuento{" "}
              {discountDurationLabel}. El precio con descuento se muestra abajo en los planes donde aplica.
            </p>
          ) : null}
          {hasCoreInFlight ? (
            <p className={css({ fontSize: "xs", color: "#94a3b8", mt: "2" })}>
              Ya tenés un plan en curso — este código se va a poder usar en tu próximo upgrade o renovación.
            </p>
          ) : null}
        </div>

        {!hasCoreInFlight ? (
          <p className={css({ fontSize: "xs", color: "#94a3b8", textAlign: "center", lineHeight: "1.6" })}>
            Vas a completar el pago en Mercado Pago, de forma segura — SFTURNOS nunca ve los datos
            de tu tarjeta. Podés cancelar en cualquier momento antes de confirmar.
          </p>
        ) : null}

        {/* Comparativa de planes — primero: es la decisión principal (Free/Pro/Enterprise).
            La calculadora de Enterprise queda debajo y colapsada por defecto para no competir
            visualmente con "Elegir Pro" cuando el usuario busca activar Pro. */}
        <div>
          <p className={css({ fontSize: "sm", fontWeight: "semibold", color: "#334155", mb: "3" })}>Comparativa de planes</p>
          <div className={css({ display: "flex", flexDir: "column", gap: "2" })}>
            {(["FREE", "PRO", "ENTERPRISE"] as const).map((key) => {
              const p = planData[key];
              const isRecommended = key === "PRO" && !hasCoreInFlight;
              return (
                <div
                  key={key}
                  ref={key === "PRO" ? proRowRef : undefined}
                  className={cx(
                    cf.dashSurface,
                    css({
                      p: "4",
                      borderLeftWidth: "3px",
                      borderLeftColor: key === plan ? p.color : isRecommended ? p.color : "transparent",
                      borderWidth: isRecommended ? "1px" : undefined,
                      borderColor: isRecommended ? "token(colors.brand.200)" : undefined,
                      boxShadow: isRecommended ? "0 0 0 1px token(colors.brand.100)" : undefined,
                      opacity: key === plan || isRecommended ? 1 : 0.75,
                    }),
                  )}
                >
                  <div className={css({ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "3" })}>
                    <div>
                      <p className={css({ fontWeight: "semibold", color: "#0f172a", display: "flex", alignItems: "center" })}>
                        {p.label}
                        {key === plan && (
                          <span className={css({ ml: "2", fontSize: "xs", color: "#64748b" })}>(plan actual)</span>
                        )}
                        {isRecommended && key !== plan && (
                          <span
                            className={css({
                              ml: "2",
                              fontSize: "xs",
                              fontWeight: "bold",
                              px: "2",
                              py: "0.5",
                              rounded: "full",
                              bg: "token(colors.brand.50)",
                              color: "token(colors.brand.600)",
                            })}
                          >
                            Recomendado
                          </span>
                        )}
                      </p>
                      <p className={css({ fontSize: "xs", color: "#64748b", mt: "0.5" })}>{p.agendas} · {p.bookings}</p>
                      <ul className={css({ mt: "2", display: "flex", flexWrap: "wrap", gap: "1.5" })}>
                        {p.features.map((f) => (
                          <li key={f} className={css({ fontSize: "xs", bg: "#f1f5f9", color: "#475569", px: "2", py: "0.5", rounded: "full" })}>
                            {f}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {key === "PRO" ? (
                      <button
                        type="button"
                        disabled={busy !== null || hasCoreInFlight}
                        onClick={() => void subscribeTo("pro")}
                        className={css({
                          flexShrink: 0,
                          fontSize: "sm",
                          fontWeight: "semibold",
                          color: "white",
                          bg: hasCoreInFlight ? "#94a3b8" : "token(colors.brand.600)",
                          px: "4",
                          py: "2",
                          rounded: "lg",
                          border: "none",
                          cursor: busy !== null || hasCoreInFlight ? "not-allowed" : "pointer",
                          transition: "transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
                          _active: { transform: hasCoreInFlight ? "none" : "scale(0.97)" },
                        })}
                      >
                        {isProActive
                          ? "Plan actual"
                          : hasCoreInFlight
                            ? "Ya tenés un plan"
                            : busy === "pro"
                              ? "Procesando…"
                              : proPriceWithDiscount != null
                                ? `Elegir Pro · ${formatPrice(proPriceWithDiscount)}/mes (antes ${formatPrice(proPrice as number)})`
                                : proPrice != null
                                  ? `Elegir Pro · ${formatPrice(proPrice)}/mes`
                                  : "Elegir Pro"}
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Calculadora / resumen Enterprise — colapsada salvo que ya sea el plan activo o el
            usuario haya llegado buscando Enterprise explícitamente (?highlight=enterprise). */}
        <div className={cx(cf.dashSurface, css({ p: "6" }))}>
          <button
            type="button"
            onClick={() => setEnterpriseOpen((v) => !v)}
            className={css({
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              bg: "transparent",
              border: "none",
              cursor: "pointer",
              textAlign: "left",
              p: 0,
            })}
            aria-expanded={enterpriseOpen}
          >
            <span className={css({ display: "flex", alignItems: "center", gap: "2" })}>
              <span
                className={css({
                  fontSize: "xs",
                  fontWeight: "bold",
                  px: "2",
                  py: "0.5",
                  rounded: "full",
                  bg: "#ede9fe",
                  color: "#7c3aed",
                })}
              >
                Enterprise
              </span>
              <span className={css({ fontSize: "sm", fontWeight: "semibold", color: "#0f172a" })}>
                {isEnterpriseActive ? "Tu plan Enterprise" : "¿Necesitás 4 o más agendas?"}
              </span>
            </span>
            <span className={css({ fontSize: "xs", color: "#7c3aed", fontWeight: "semibold" })}>
              {enterpriseOpen ? "Ocultar ▲" : "Ver plan ▾"}
            </span>
          </button>

          {enterpriseOpen ? (
          <div className={css({ mt: "4" })}>
          {isEnterpriseActive ? (
            <>
              <p className={css({ fontWeight: "semibold", color: "#0f172a", mb: "1" })}>Tu plan Enterprise</p>
              <p className={css({ fontSize: "sm", color: "#64748b", lineHeight: "1.6", mb: "4" })}>
                Tenés {sub.agendasAllowed ?? ENTERPRISE_INCLUDED_AGENDAS} agendas contratadas. Ajustá la cantidad si necesitás más o menos.
              </p>
            </>
          ) : (
            <>
              <p className={css({ fontWeight: "semibold", color: "#0f172a", mb: "1" })}>¿Necesitás más de 3 agendas?</p>
              <p className={css({ fontSize: "sm", color: "#64748b", lineHeight: "1.6", mb: "4" })}>
                El plan Enterprise incluye 3 agendas base y se suma{" "}
                <strong>{formatPrice(enterpriseExtraPrice)} / agenda extra</strong> por mes.
                Precio base: <strong>{formatPrice(enterpriseBasePrice)} / mes</strong>.
              </p>
            </>
          )}

          <label className={css({ display: "flex", flexDir: "column", gap: "2" })}>
            <span className={css({ fontSize: "sm", fontWeight: "semibold", color: "#334155" })}>
              Cantidad de agendas
            </span>
            <div className={css({ display: "flex", alignItems: "center", gap: "3" })}>
              <button
                type="button"
                onClick={() => setEnterpriseAgendas((v) => Math.max(ENTERPRISE_MIN_AGENDAS, v - 1))}
                className={css({
                  w: "9", h: "9", rounded: "full",
                  borderWidth: "1px", borderColor: "#e2e8f0",
                  bg: "#f8fafc", fontWeight: "bold", fontSize: "lg",
                  cursor: enterpriseAgendas <= ENTERPRISE_MIN_AGENDAS ? "not-allowed" : "pointer",
                  opacity: enterpriseAgendas <= ENTERPRISE_MIN_AGENDAS ? 0.4 : 1,
                  _hover: { bg: "#f1f5f9" },
                })}
              >
                −
              </button>
              <span className={css({ fontSize: "xl", fontWeight: "bold", color: "#0f172a", minW: "3rem", textAlign: "center" })}>
                {enterpriseAgendas}
              </span>
              <button
                type="button"
                onClick={() => setEnterpriseAgendas((v) => v + 1)}
                className={css({
                  w: "9", h: "9", rounded: "full",
                  borderWidth: "1px", borderColor: "#e2e8f0",
                  bg: "#f8fafc", fontWeight: "bold", fontSize: "lg",
                  cursor: "pointer",
                  _hover: { bg: "#f1f5f9" },
                })}
              >
                +
              </button>
            </div>
          </label>

          <div className={css({ mt: "4", p: "4", rounded: "lg", bg: "#faf5ff", borderWidth: "1px", borderColor: "#e9d5ff" })}>
            <p className={css({ fontSize: "xs", color: "#7c3aed", mb: "1" })}>Costo mensual estimado</p>
            {enterpriseMonthlyPriceWithDiscount != null ? (
              <p className={css({ display: "flex", alignItems: "baseline", gap: "2" })}>
                <span className={css({ fontSize: "sm", color: "#a78bfa", textDecoration: "line-through" })}>
                  {formatPrice(enterpriseMonthlyPrice)}
                </span>
                <span className={css({ fontSize: "2xl", fontWeight: "bold", color: "#6d28d9" })}>
                  {formatPrice(enterpriseMonthlyPriceWithDiscount)}
                </span>
                <span className={css({ fontSize: "sm", fontWeight: "normal", color: "#7c3aed" })}>/ mes</span>
              </p>
            ) : (
              <p className={css({ fontSize: "2xl", fontWeight: "bold", color: "#6d28d9" })}>
                {formatPrice(enterpriseMonthlyPrice)} <span className={css({ fontSize: "sm", fontWeight: "normal", color: "#7c3aed" })}>/ mes</span>
              </p>
            )}
            <p className={css({ fontSize: "xs", color: "#8b5cf6", mt: "1" })}>
              {ENTERPRISE_INCLUDED_AGENDAS} agendas incluidas + {enterpriseExtraAgendas} extra{enterpriseExtraAgendas !== 1 ? "s" : ""}{" "}
              × {formatPrice(enterpriseExtraPrice)}
            </p>
          </div>

          {isEnterpriseActive ? (
            <button
              type="button"
              disabled={busy !== null || enterpriseAgendas === (sub.agendasAllowed ?? ENTERPRISE_INCLUDED_AGENDAS)}
              onClick={() => void updateEnterpriseAgendas()}
              className={css({
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                mt: "4", w: "full", py: "3", rounded: "lg", fontSize: "sm", fontWeight: "semibold",
                color: "white", bg: "#7c3aed", border: "none",
                cursor: busy !== null ? "not-allowed" : "pointer",
                opacity: busy !== null || enterpriseAgendas === (sub.agendasAllowed ?? ENTERPRISE_INCLUDED_AGENDAS) ? 0.55 : 1,
                _hover: { bg: "#6d28d9" },
              })}
            >
              {busy === "enterprise-update" ? "Actualizando…" : "Actualizar agendas"}
            </button>
          ) : (
            <>
              <button
                type="button"
                disabled={busy !== null || isProActive}
                onClick={() => void subscribeTo("enterprise")}
                className={css({
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  mt: "4", w: "full", py: "3", rounded: "lg", fontSize: "sm", fontWeight: "semibold",
                  color: "white", bg: "#7c3aed", border: "none",
                  cursor: busy !== null || isProActive ? "not-allowed" : "pointer",
                  opacity: busy !== null || isProActive ? 0.55 : 1,
                  _hover: { bg: "#6d28d9" },
                })}
              >
                {busy === "enterprise" ? "Procesando…" : "Solicitar upgrade a Enterprise"}
              </button>
              {isProActive ? (
                <p className={css({ fontSize: "xs", color: "#94a3b8", mt: "2" })}>
                  Ya tenés el plan Pro activo. Cancelalo primero para pasar a Enterprise.
                </p>
              ) : null}
            </>
          )}
          </div>
          ) : null}
        </div>

        <p className={css({ fontSize: "sm", color: "#94a3b8", lineHeight: "1.6" })}>
          ¿Necesitás un plan a medida o tenés dudas con tu pago? Escribinos a{" "}
          <a href="mailto:turnos@sftdevelopment.com" className={css({ color: "token(colors.brand.600)", textDecoration: "underline" })}>
            turnos@sftdevelopment.com
          </a>
        </p>
      </div>
    </DashboardPageShell>
  );
}
